import { isAnonymous } from 'common-data-handling';
import _, { negate } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFilteredParticipants } from '../../../../data/hooks/participant/useFilteredParticipants';
import LoadEventConnections from '../../../loaders/LoadEventConnections';
import LoadParticipants from '../../../loaders/LoadParticipants';
import ErrorBoundary from '../../../validate/ErrorBoundary';
import BubblesFilter from './BubblesFilter/BubblesFilter';
import BubblesFilterState from './BubblesFilterState/BubblesFilterState';
import BubbleWrapper from './BubbleWrapper/BubbleWrapper';
import InnerCircleCenterBubble from './InnerCircleCenterBubble/InnerCircleCenterBubble';
import styles from './ParticipantBubbles.module.scss';
import { useProtectedRegions } from './useProtectedRegions';


const BUBBLE_SIZE = 70,
    EXTRA_SPACE = 50,
    CONTAINER_PADDING = 0,
    INFO_RIGHT_LIMIT = 200;

function filterOnLoginTime(now, a, b) {
    let bTime = b.status === 'online' ? now : moment(b.last_login || 0);
    let aTime = a.status === 'online' ? now : moment(a.last_login || 0);
    return bTime.diff(aTime);
}
function inRange(a, b, extraSpace = 0) {
    let distanceX = a.x - b.x,
        distanceY = a.y - b.y,
        distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
    return distance <= a.size / 2 + b.size / 2 + extraSpace;
}

function isBubbleInside(bubble, rect) {
    let margin = bubble.size / 2;
    return bubble.x >= rect.left - margin && bubble.x <= rect.right + margin && bubble.y >= rect.top - margin && bubble.y <= rect.bottom + margin;
}

const composeParticipantId = (user) => [user.user_id, user.participant_id].join();

const ParticipantBubbles = (props) => {
    const { session, event } = props,
        // fullScreen = useSidePanel().maximize,
        // i18n = useI18n(),
        // { primary_color, secondary_color } = event.settings,
        [containerStyle, setContainerStyle] = useState({ width: 0, height: 0 }),
        [mount, setMount] = useState(),
        [bubbles, setBubbles] = useState([]),
        // [width, height] = useWindowSize(),
        [filteredParticipants, filterParams] = useFilteredParticipants(event.id, null, {
            filter: useCallback(negate(isAnonymous), []),
        }),
        indexedParticipants = useMemo(() => {
            const result = {};
            for (let p of filteredParticipants) {
                result[composeParticipantId(p)] = p;
            }
            return result;
        }, [filteredParticipants]),
        participants = useMemo(() => {
            let result;
            if (session && session.is_selectable && session.is_selected) {
                result = filteredParticipants.filter((participant) => _.includes(participant.program_selection, session.id));
            } else {
                result = filteredParticipants; //.filter(p => !!p.user_id);
            }
            // result.sort(filterOnLoginTime.bind(this, moment())); // ignoring order  -  we will pick participants in a more random way when assigning bubbles
            return result;
        }, [filteredParticipants, session]),
        [expandedBubble, setExpandedBubble] = useState(),
        // [zIndex, setZIndex] = useState(1),
        containerRef = useRef(),
        ref = useRef(),
        // containerRect = useElementRect(containerRef, null),
        [protectedRegions, updateProtectedRegion] = useProtectedRegions({
            containerRef,
        });


    const handleExpandBubble = useCallback(
        (userId) => {
            if (userId != expandedBubble) {
                // const nextZIndex = zIndex + 1;
                setExpandedBubble(userId);
                setBubbles(bubbles.map((bubble, index) => (participants[index]?.user_id == userId ? { ...bubble /*, zIndex: nextZIndex*/ } : bubble)));
                // setZIndex(nextZIndex);
            }
        },
        [expandedBubble, bubbles /*, zIndex */]
    );

    const handleExpandFilterMenu = useCallback(() => setExpandedBubble(null), []);

    let bubbleCount = props.bubbleCount || participants.length || 50,
        limitedTries = 10000;

    // save the container size in containerStyle
    useEffect(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            if (rect.width > 0 && rect.width != containerStyle.width && rect.height > 0 && rect.height != containerStyle.height) {
                setMount(true);
                setContainerStyle({
                    width: rect.width,
                    height: rect.height,
                });
                setBubbles([]);
            }
            // updateProtectedRegion('DEBUG', rect)
        }
    });

    useEffect(() => {
        // Generate bubble object that are place randomly within the container
        // https://stackoverflow.com/questions/21029371/why-doesnt-this-function-detect-overlapping-circles
        if (mount && containerStyle && containerStyle.height > 0 && containerStyle.width > 0) {
            let _bubbles = [...bubbles],
                bubblesChanged = false,
                containerWidth = containerStyle.width - 2 * CONTAINER_PADDING,
                containerHeight = containerStyle.height - 2 * CONTAINER_PADDING;

            // Attempt to add bubbles until area is full
            for (let i = 0; _bubbles.length < bubbleCount && i < limitedTries; i++) {
                let overLap = false;
                const bubble = {
                    uid: uuidv4(),
                    x: _.random(containerWidth, true) + CONTAINER_PADDING,
                    y: _.random(containerHeight, true) + CONTAINER_PADDING,
                    size: BUBBLE_SIZE,
                };

                for (let i = 0; i < _bubbles.length; i++) {
                    const isInRange = inRange(bubble, _bubbles[i], EXTRA_SPACE);
                    if (isInRange) {
                        overLap = true;
                        break;
                    }
                }
                if (!overLap) {
                    _bubbles.push(bubble);
                    bubblesChanged = true;
                }
            }
            if (bubblesChanged) {
                setBubbles(_bubbles);
            }
        }
    }, [containerStyle, bubbleCount, bubbles]);

    // disable bubbles blocked by protected regions
    useEffect(() => {
        let changed = false,
            nextBubbles = bubbles.map((b) => {
                let blocked = !!protectedRegions.find((region) => isBubbleInside(b, region));
                if (blocked != b.blocked) {
                    changed = true;
                    return {
                        ...b,
                        blocked,
                    };
                }
                return b;
            });
        if (changed) setBubbles(nextBubbles);
    }, [bubbles, protectedRegions]);

    // from time to time, free some of the bubbles for rotation purposes
    useEffect(() => {
        let interval = setInterval(() => {
            const numberOfBubblesToRotate = _.random(Math.round(bubbles.length / 10)),
                nextBubbles = bubbles.map((b, i) => (b.rotation ? { ...b, rotation: false } : b));

            // console.log('Bubbles rotation', numberOfBubblesToRotate);
            for (let i = 0; i < numberOfBubblesToRotate; ++i) {
                let index = _.random(bubbles.length);
                if (nextBubbles[index] && (!expandedBubble || expandedBubble != nextBubbles[index]?.participant?.user_id)) {
                    // console.log('rotate', index);
                    nextBubbles[index] = {
                        ...nextBubbles[index],
                        rotation: true,
                    };
                }
            }

            setBubbles(nextBubbles);
        }, 10000);
        return () => clearInterval(interval);
    }, [bubbles]);

    // assign participants to bubbles
    useEffect(() => {
        let changed = false,
            nextBubbles = bubbles.map((b) => {
                // remove participants that were removed or in blocked bubbles
                if (b.participant && (b.blocked || b.rotation || !indexedParticipants[b.participant])) {
                    changed = true;
                    return {
                        ...b,
                        participant: null,
                    };
                }
                return b;
            }),
            assignedParticipants = nextBubbles.map((b) => b.participant).filter((i) => i),
            unassignedParticipants = _.sortBy(
                participants.filter((p) => !assignedParticipants.includes(composeParticipantId(p))),
                [(user) => (user.status === 'online' ? 0 : user.status === 'offline' ? 1 : 2), () => Math.random()]
            );

        // add unassigned participants
        for (let p of unassignedParticipants) {
            let bubbleIndex = nextBubbles.findIndex((b) => !b.participant && !b.blocked && !b.rotation);
            if (bubbleIndex === -1) break;
            nextBubbles[bubbleIndex] = {
                ...nextBubbles[bubbleIndex],
                participant: composeParticipantId(p),
            };
            changed = true;
        }
        if (changed) {
            // console.log('NEXT BUBBLES', nextBubbles.filter(b => b.participant));
            setBubbles(nextBubbles);
        }
    }, [bubbles, participants]);

    // makes sure networkarea zIndex is higher when overing one of the bubbles
    const [isMouseOver, setMouseOver] = useState(false),
        handleMouseOver = useCallback(() => {
            setMouseOver(true);
        }),
        handleMouseOut = useCallback(() => {
            setMouseOver(false);
        });

    // Keep sidepanel over app screen when mouse is over
    useEffect(() => {
        if (isMouseOver) {
            document.body.classList.add('raise-splitScreen');
        }
        return () => {
            if (isMouseOver && !expandedBubble) {
                document.body.classList.remove('raise-splitScreen');
            }
        };
    }, [isMouseOver, expandedBubble]);

    // Unexpand bubble if a click is detected on the app side
    useEffect(() => {
        function handleClickDetectedOnAppSide() {
            setExpandedBubble(null);
            document.body.classList.remove('raise-splitScreen');
        }
        window.addEventListener('blur', handleClickDetectedOnAppSide);
        document.querySelector('#networkapp').addEventListener('click', handleClickDetectedOnAppSide);
        return () => {
            window.removeEventListener('blur', handleClickDetectedOnAppSide);
            document.querySelector('#networkapp').removeEventListener('click', handleClickDetectedOnAppSide);
        };
    }, []);

    return (
        <>
            <InnerCircleCenterBubble
                className={styles.innerCircleCenter}
                event={event}
                filterParams={filterParams}
                onProtectRegion={updateProtectedRegion}
                style={
                    ref.current
                        ? {
                              top: ref.current.offsetTop,
                              left: ref.current.offsetLeft,
                              width: ref.current.offsetWidth,
                              height: ref.current.offsetHeight,
                          }
                        : null
                }
            />
            <div className={styles.ParticipantBubbles} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut} onClick={() => setExpandedBubble(null)} ref={ref}>
                <div className={styles.container} ref={containerRef}>
                    {bubbles.map((bubble, index) => {
                        let infoPosition;
                        if (bubble.x + INFO_RIGHT_LIMIT > containerStyle.width) {
                            infoPosition = bubble.y < containerStyle.height / 2 ? 'bottom' : 'top';
                        }
                        return (
                            <BubbleWrapper
                                key={bubble.uid}
                                bubble={bubble}
                                event={event}
                                user={indexedParticipants[bubble.participant]}
                                infoPosition={infoPosition}
                                expandedBubble={expandedBubble}
                                onExpandBubble={handleExpandBubble}
                            />
                        );
                    })}
                </div>
                <BubblesFilterState className={styles.BubblesFilterState} event={event} filterParams={filterParams} onProtectRegion={updateProtectedRegion} />
                <BubblesFilter
                    className={styles.BubblesFilter}
                    event={event}
                    filterParams={filterParams}
                    expandedBubble={expandedBubble}
                    onExpandFilterMenu={handleExpandFilterMenu}
                    onProtectRegion={updateProtectedRegion}
                />
            </div>
        </>
    );
};

export default (props) => {
    const { event } = props;
    return (
        <ErrorBoundary>
            <LoadParticipants eventId={event.id} skipSpinner>
                <LoadEventConnections eventId={event.id} skipSpinner>
                    <ParticipantBubbles {...props} />
                </LoadEventConnections>
            </LoadParticipants>
        </ErrorBoundary>
    );
};
